(() => {
  'use strict';

  const FIELD_PREFIX_RE = /^(title|body|tag|product_type|vendor|variants\.(title|sku|barcode)):/i;

  // Enabled search fields, in priority order. `prefix` is the Shopify
  // search operator for that field. Single source of truth: the global
  // Theme settings (window.theme.searchFields, from settings_data.json).
  const FIELD_DEFS = [
    { key: 'title', prefix: 'title' },
    { key: 'description', prefix: 'body' },
    { key: 'tags', prefix: 'tag' },
  ];

  // Stop words are not indexed by Shopify as searchable tokens. Forcing
  // them into a field query (title:the) makes the whole query return
  // zero results, so they are dropped from the effective search terms.
  const STOP_WORDS = new Set(['the', 'a', 'an']);

  const MAX_WORDS = 5;
  const MAX_PREDICTIVE_PRODUCTS = 10;

  function getSearchFields() {
    const fields = window.theme?.searchFields || {};
    return {
      title: Boolean(fields.title),
      description: Boolean(fields.description),
      tags: Boolean(fields.tags),
    };
  }

  function enabledFieldDefs(fields = getSearchFields()) {
    return FIELD_DEFS.filter((def) => fields[def.key]);
  }

  function stripScopedQuery(term) {
    if (!term) return '';
    let value = String(term).trim();
    if (!value) return '';

    // title:fox OR title:collar → fox collar
    value = value
      .replace(/\s+OR\s+/gi, ' ')
      .replace(/(?:^|\s)(?:title|body|tag|product_type|vendor|variants\.(?:title|sku|barcode)):/gi, ' ')
      .replace(/"/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Dedupe words while preserving order
    const seen = new Set();
    const words = [];
    value.split(' ').forEach((word) => {
      const key = word.toLowerCase();
      if (!word || seen.has(key)) return;
      seen.add(key);
      words.push(word);
    });

    return words.join(' ');
  }

  // Words that are actually searched: scoped prefixes removed, hyphens
  // treated as separators (a bare "-" is Shopify's exclusion operator),
  // edge punctuation trimmed and stop words dropped.
  function getEffectiveWords(rawTerm) {
    const term = stripScopedQuery(rawTerm || '');
    if (!term) return [];

    const seen = new Set();
    const words = [];
    term
      .toLowerCase()
      .replace(/-/g, ' ')
      .split(/\s+/)
      .forEach((raw) => {
        const word = raw.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
        if (!word || STOP_WORDS.has(word) || seen.has(word)) return;
        seen.add(word);
        words.push(word);
      });

    return words.slice(0, MAX_WORDS);
  }

  // Plain-text normalization for form submits: trim, collapse spaces,
  // "-" → space (a bare "-" excludes results natively) and drop quotes.
  // The results page receives the plain term and filters server-side.
  function normalizeSearchText(value) {
    return String(value || '')
      .replace(/"/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function productKeyFromItem(item) {
    const link = item.querySelector('a[data-product-link], a[href*="/products/"]');
    const href = link?.getAttribute('href') || '';
    if (!href) return '';
    try {
      return new URL(href, window.location.origin).pathname;
    } catch (e) {
      return href.split('?')[0];
    }
  }

  // Runs `loadItems(query)` once per word/field combination.
  // Results are unioned across fields per word, then intersected across
  // words: a product must match every word in at least one enabled field.
  async function collectScopedItems(words, defs, loadItems) {
    const perWord = [];

    for (const word of words) {
      const lists = await Promise.all(
        defs.map(async (def) => {
          try {
            return await loadItems(`${def.prefix}:${word}`);
          } catch (error) {
            if (error?.name === 'AbortError' || error?.code === 20) throw error;
            return [];
          }
        })
      );

      const byKey = new Map();
      lists.flat().forEach((item) => {
        const key = productKeyFromItem(item);
        if (key && !byKey.has(key)) byKey.set(key, item);
      });
      perWord.push(byKey);
    }

    if (!perWord.length) return [];

    const [first, ...rest] = perWord;
    return [...first.entries()]
      .filter(([key]) => rest.every((map) => map.has(key)))
      .map(([, item]) => item);
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  function normalizeVisibleInputs() {
    document.querySelectorAll('form.search-form input[type="search"]').forEach((input) => {
      if (input.name !== 'q' && !input.hasAttribute('data-search-display')) return;
      const display = stripScopedQuery(input.value);
      if (display !== input.value) input.value = display;
    });
  }

  function cleanPredictiveLabels(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-predictive-search-search-for-text]').forEach((el) => {
      const text = el.textContent || '';
      const cleaned = text.replace(/title:|body:|tag:/gi, '');
      if (cleaned !== text) el.textContent = cleaned;
    });
  }

  function onSearchFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('search-form')) return;

    const input = form.querySelector('input[type="search"][name="q"], input[type="search"][data-search-display]');
    if (!input) return;

    // Respect queries the user already wrote with a field prefix.
    if (FIELD_PREFIX_RE.test((input.value || '').trim())) return;

    const normalized = normalizeSearchText(input.value);
    if (!normalized) {
      event.preventDefault();
      return;
    }
    input.value = normalized;
  }

  // NOTE: Shopify caps predictive search at 10 products per query and
  // resources[limit] does not raise it. The merge below therefore works
  // on each query's top-10 set; that cap is inherent to predictive
  // search (the full results page has no such limit).
  async function fetchPredictiveDoc(query, signal) {
    const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=predictive-search`;
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(response.status);
    return parseHTML(await response.text());
  }

  function buildPredictiveMarkup(docsByQuery, items, displayTerm) {
    const docs = [...docsByQuery.values()];
    const baseDoc =
      docs.find((doc) => doc.querySelector('.predictive-search__grid-item')) || docs[0];
    if (!baseDoc) throw new Error('No predictive responses');

    const section = baseDoc.querySelector('#shopify-section-predictive-search');
    if (!section) throw new Error('No predictive section');

    const baseQuery = docsByQuery.keys().next().value || '';

    // Show the term the user typed, not the scoped query.
    const searchFor = section.querySelector('[data-predictive-search-search-for-text]');
    if (searchFor && baseQuery) {
      searchFor.textContent = searchFor.textContent.replace(baseQuery, displayTerm);
    }

    // Drop the suggestions column (queries/collections/pages/articles):
    // scoped field searches only make sense for products.
    section
      .querySelectorAll('.predictive-search__layout > .predictive-search__column')
      .forEach((column) => {
        if (!column.querySelector('[data-search-results]')) column.remove();
      });

    const layout = section.querySelector('.predictive-search__layout');
    if (layout) layout.classList.add('predictive-search__layout--no-suggestions');

    const grid = section.querySelector('[data-search-results] .grid');
    if (!items.length) {
      if (layout) layout.remove();
    } else if (grid) {
      grid.innerHTML = items
        .slice(0, MAX_PREDICTIVE_PRODUCTS)
        .map((item) => item.outerHTML)
        .join('');
    }

    // Keep the (visually hidden) live region honest about the outcome.
    const liveRegion = section.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      if (!items.length) {
        const emptyDoc = docs.find((doc) => !doc.querySelector('.predictive-search__grid-item'));
        const emptySection = emptyDoc?.querySelector('#shopify-section-predictive-search');
        const emptyText = emptySection?.querySelector(
          '[data-predictive-search-live-region-count-value]'
        )?.textContent;
        const emptyQuery = [...docsByQuery.keys()][
          [...docsByQuery.values()].indexOf(emptyDoc)
        ];
        if (emptyText) {
          liveRegion.textContent = emptyQuery
            ? emptyText.replace(emptyQuery, displayTerm)
            : emptyText;
        }
      } else if (baseQuery) {
        liveRegion.textContent = liveRegion.textContent.replace(baseQuery, displayTerm);
      }
      liveRegion.textContent = liveRegion.textContent.replace(/title:|body:|tag:/gi, '');
    }

    return section.innerHTML;
  }

  function patchPredictiveSearch() {
    const Ctor = customElements.get('predictive-search');
    if (!Ctor || Ctor.prototype.__searchFieldsPatched) return;

    const proto = Ctor.prototype;
    const originalGetSearchResults = proto.getSearchResults;
    const originalRenderSearchResults = proto.renderSearchResults;

    proto.getSearchResults = function getSearchResults(searchTerm) {
      const defs = enabledFieldDefs();
      const words = getEffectiveWords(searchTerm);

      // No scoped fields (or nothing searchable left after dropping stop
      // words): keep the default Shopify behaviour.
      if (!defs.length || !words.length || FIELD_PREFIX_RE.test((searchTerm || '').trim())) {
        return originalGetSearchResults.call(this, searchTerm);
      }

      const displayTerm = stripScopedQuery(searchTerm);
      const queryKey = `scoped|${defs.map((def) => def.key).join(',')}|${words.join(' ')}`;

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      this.setLiveRegionLoadingState();
      const signal = this.abortController ? this.abortController.signal : undefined;

      (async () => {
        const docsByQuery = new Map();
        const items = await collectScopedItems(words, defs, async (query) => {
          const doc = await fetchPredictiveDoc(query, signal);
          docsByQuery.set(query, doc);
          const section = doc.querySelector('#shopify-section-predictive-search');
          return section ? [...section.querySelectorAll('.predictive-search__grid-item')] : [];
        });

        const markup = buildPredictiveMarkup(docsByQuery, items, displayTerm);

        // A newer keystroke already replaced this search: don't render
        // stale results over it.
        if (this.searchTerm && this.searchTerm !== searchTerm) return;

        if (this.allPredictiveSearchInstances) {
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = markup;
          });
        }
        this.cachedResults[queryKey] = markup;
        this.renderSearchResults(markup);
      })().catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        originalGetSearchResults.call(this, searchTerm);
      });
    };

    if (typeof originalRenderSearchResults === 'function') {
      proto.renderSearchResults = function renderSearchResults(resultsMarkup) {
        const result = originalRenderSearchResults.call(this, resultsMarkup);
        cleanPredictiveLabels(this);
        return result;
      };
    }

    proto.__searchFieldsPatched = true;
  }

  function syncThemeSearchFields() {
    window.theme = window.theme || {};
    window.theme.searchFields = getSearchFields();
    return window.theme.searchFields;
  }

  function init() {
    window.theme = window.theme || {};
    syncThemeSearchFields();

    window.theme.stripScopedSearchQuery = stripScopedQuery;
    window.theme.getSearchFields = getSearchFields;

    normalizeVisibleInputs();
    cleanPredictiveLabels();
    document.addEventListener('submit', onSearchFormSubmit, true);

    if (customElements.get('predictive-search')) {
      patchPredictiveSearch();
    } else {
      customElements.whenDefined('predictive-search').then(patchPredictiveSearch);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
